import { Box, Button, styled } from '@mui/material';

export const StyledDatePickerButton = styled(Button, {
  shouldForwardProp: (prop) =>
    ![
      'isError',
      'isNewField',
      'disabled',
      'isTemplatePage',
      'isDatePopupOpen',
      'isQueuePage',
      'isFloatingTicketPanel',
      'isTicketExists',
      'isEmpty',
    ].includes(prop),
})(
  ({
    isError,
    theme,
    isNewField,
    disabled,
    isTemplatePage,
    isDatePopupOpen,
    isQueuePage,
    isFloatingTicketPanel,
    isTicketExists,
    isEmpty,
  }) => {
    let color;
    if (isError && !isDatePopupOpen) {
      color = theme.palette.labels.bt_red_reg;
    } else if (isQueuePage && isEmpty) {
      color = theme?.palette?.grey?.bt_def_grey_reg;
    } else {
      color = theme.palette.text.txt_default;
    }
    let backgroundColor = isError && !isDatePopupOpen ? theme.palette.labels.bt_red_light : 'inherit';
    const hoveredErrorColor = isError && theme.palette.labels.bt_red_dark;
    let hoveredBackground = isError ? theme.palette.labels.bt_red_mid : theme.palette.labels.bt_black_light;
    let dateFieldBorderStyle =
      isQueuePage && !isFloatingTicketPanel ? 'transparent' : theme?.palette?.blueLabels?.bt_blue_field_empty_hover;
    let padding = isTicketExists || isEmpty ? '4px 8px' : '0px';
    let lineHeight = '18px';
    let hoveredBorder;

    const placeholderColor = (() => {
      if (isQueuePage) {
        return theme?.palette?.grey?.bt_def_grey_reg;
      }
      if (!isTicketExists) {
        return theme?.palette?.text?.txt_default;
      }
      if (disabled) {
        return theme?.palette?.grey?.chip_grey;
      }
      if (isError) {
        return theme?.palette?.labels?.bt_red_reg;
      }
      return theme?.palette?.blueLabels?.bt_blue_chip_reg;
    })();

    if (isTemplatePage) {
      padding = '6px 8px';
      lineHeight = '22px';
    }

    if (disabled) {
      color = theme?.palette?.grey?.chip_grey;
    }

    if (isNewField && !isError && !isTemplatePage && !disabled) {
      backgroundColor = theme?.palette?.blueLabels?.bt_blue_field_empty;
      hoveredBackground = theme?.palette?.blueLabels?.bt_blue_field_empty_hover;
    }

    if (isTicketExists) {
      color = theme?.palette?.blueLabels?.bt_status_reg;
      backgroundColor = isError
        ? theme.palette.labels.bt_red_light
        : `${theme?.palette?.blueLabels?.bt_blue_field_empty} !important`;
      hoveredBorder = isError
        ? `1px solid ${theme.palette.labels.bt_red_reg}`
        : `1px solid ${theme?.palette?.blueLabels?.bt_blue_chip_light}`;
      hoveredBackground = `${theme?.palette?.blueLabels?.bt_blue_field_empty_hover} !important`;
      dateFieldBorderStyle = isError ? theme.palette.labels.bt_red_reg : dateFieldBorderStyle;
      if (disabled) {
        color = theme?.palette?.grey?.chip_grey;
        backgroundColor = 'transparent';
        hoveredBackground = 'transparent !important';
        dateFieldBorderStyle = theme?.palette?.grey?.divider_grey_v1;
        hoveredBorder = `1px solid ${theme?.palette?.grey?.divider_grey_v1}`;
      }
    }

    const hoveredStyles = {
      '&:hover': {
        color: isError ? hoveredErrorColor : color,
        background: isError ? theme.palette.labels.bt_red_mid : hoveredBackground,
        border: !isError && hoveredBorder,
        '& .warning-icon *': {
          fill: hoveredErrorColor,
        },
        '& .openPickerIcon': {
          stroke: isError ? hoveredErrorColor : color,
          filter:
            isError &&
            'brightness(0) saturate(100%) invert(23%) sepia(20%) saturate(5553%) hue-rotate(338deg) brightness(99%) contrast(96%)',
        },
      },
    };

    const expandedStyles = {
      ...(!disabled && {
        border: '1px solid #CCC',
      }),
      '& span[data-error-tooltip=true], .warning-icon': {
        display: 'none',
      },
    };

    return {
      borderRadius: '4px',
      border: '0px',
      color,
      fontStyle: 'normal',
      fontWeight: 400,
      fontSize: '14px',
      lineHeight,
      padding,
      minHeight: '28px !important',
      width: '110px',
      textTransform: 'none',
      transition: '0.1s',
      display: 'flex',
      cursor: disabled ? 'default' : 'pointer',
      justifyContent: 'space-between',
      background: backgroundColor,
      '&.MuiButtonBase-root': {
        fontFamily: theme.fonts.robotoRegular,
        fontSize: '14px',
        padding,
        border: `1px solid ${dateFieldBorderStyle}`,
        '&:hover': {
          border: isEmpty ? `1px solid ${theme?.palette?.blueLabels?.bt_blue_field_empty_hover}` : 'transparent',
        },
      },
      '&.Mui-disabled': {
        border: '0px',
      },
      '.empty-date-picker-placeholder': {
        color: placeholderColor,
        textOverflow: 'ellipsis',
        overflow: 'hidden',
        marginRight: '0.3rem',
        '&:hover': {
          color: isError ? theme.palette.labels.bt_red_reg : isTicketExists && !disabled && '#516C96 !important',
        },
      },
      '.openPickerIcon': {
        filter:
          isError &&
          'brightness(0) saturate(100%) invert(72%) sepia(35%) saturate(5919%) hue-rotate(317deg) brightness(97%) contrast(100%)',
      },

      '.openPickerIcon *': {
        stroke: color,
        filter: 'invert(56%) sepia(34%) saturate(388%) hue-rotate(178deg) brightness(88%) contrast(91%)',
      },
      '& .warning-icon *': {
        fill: theme.palette.labels.bt_red_reg,
      },
      '& span': {
        textAlign: 'start',
      },
      ...(isDatePopupOpen ? expandedStyles : hoveredStyles),
    };
  },
);

export const StyledDatePickerIcons = styled(Box)(() => ({
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center',
  columnGap: '4px',
  flexDirection: 'row',
  flexWrap: 'nowrap',

  '& .warning-icon': {
    display: 'flex',
    margin: '0 2px',
  },
}));
